import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowRight, ArrowLeft, CheckCircle, Phone, Mail,
  Clock, Shield, DollarSign, Sparkles, Heart,
  Car, Plane, Wine, MapPin, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════
   PERSONA ENGINE — determines result profile
   ═══════════════════════════════════════════════ */
const PERSONAS = [
  {
    id: 'driven-executive',
    name: 'The Driven Executive',
    desc: 'Your time is your most valuable currency. You need transportation that thinks ahead so you never have to.',
    match: (a) => a.identity === 'driven' && a.ratings.time >= 4,
  },
  {
    id: 'lifestyle-upgrader',
    name: 'The Lifestyle Upgrader',
    desc: "You know your auto lifestyle doesn't match your ambitions yet. You're ready for the upgrade — the right vehicle, the right experience, the right image.",
    match: (a) => a.identity === 'self' && a.painPoints.length >= 2,
  },
  {
    id: 'occasion-curator',
    name: 'The Occasion Curator',
    desc: "Life's big moments deserve excellence. You want every arrival, every exit, every detail handled with precision and class.",
    match: (a) => a.occasions.length >= 3,
  },
  {
    id: 'corporate-commander',
    name: 'The Corporate Commander',
    desc: 'Your organization moves fast. You need a transportation partner that operates at your level — reliable, scalable, and always professional.',
    match: (a) => a.workNeeds.length >= 3,
  },
  {
    id: 'flexible-navigator',
    name: 'The Flexible Navigator',
    desc: "You need options. Some days you drive, some days you're driven. ACE builds a system that adapts to your rhythm.",
    match: (a) => a.identity === 'depends',
  },
  {
    id: 'auto-explorer',
    name: 'The Auto Explorer',
    desc: "You're just starting to think about what's possible. ACE helps you discover the transportation lifestyle you didn't know existed.",
    match: () => true, // fallback
  },
];

const SOLUTIONS = {
  time: { title: 'Concierge Driver Service', desc: 'A dedicated professional behind the wheel so you own your hours.', route: '/services/concierge' },
  stress: { title: 'Personal Auto Concierge', desc: 'ACE handles everything — scheduling, routing, logistics — you just arrive.', route: '/driving' },
  cost: { title: 'ACE Membership', desc: 'Predictable monthly pricing with priority access and member rates.', route: '/memberships' },
  luxury: { title: 'Exotic & Luxury Fleet', desc: 'Access to Rolls-Royce, Bentley, and premium vehicles for any occasion.', route: '/fleet-gallery' },
  safety: { title: 'Vetted Professional Drivers', desc: 'Background-checked, trained, and trusted — every single time.', route: '/services' },
};

/* ═══════════════════════════════════════════════ */

const AutoLifestyleQuiz = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=lead, 1-6=sections, 7=results
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    identity: '',
    painPoints: [],
    frequency: '',
    workNeeds: [],
    occasions: [],
    ratings: { time: 3, stress: 3, cost: 3, luxury: 3, safety: 3 },
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm(p => {
    const a = p[k] || [];
    return { ...p, [k]: a.includes(v) ? a.filter(x => x !== v) : [...a, v] };
  });
  const setRating = (k, v) => setForm(p => ({ ...p, ratings: { ...p.ratings, [k]: v } }));

  // Determine persona
  const persona = useMemo(() => {
    return PERSONAS.find(p => p.match(form)) || PERSONAS[PERSONAS.length - 1];
  }, [form]);

  // Calculate scores
  const scores = useMemo(() => {
    const r = form.ratings;
    return [
      { key: 'time', label: 'Time Recovery', val: r.time, icon: Clock, color: '#3b82f6' },
      { key: 'stress', label: 'Stress Reduction', val: r.stress, icon: Heart, color: '#8b5cf6' },
      { key: 'cost', label: 'Cost Management', val: r.cost, icon: DollarSign, color: '#22c55e' },
      { key: 'luxury', label: 'Luxury & Experience', val: r.luxury, icon: Sparkles, color: '#eab308' },
      { key: 'safety', label: 'Safety & Trust', val: r.safety, icon: Shield, color: '#ef4444' },
    ];
  }, [form.ratings]);

  // Top recommended solutions based on highest-rated priorities
  const topSolutions = useMemo(() => {
    return scores
      .sort((a, b) => b.val - a.val)
      .slice(0, 3)
      .map(s => ({ ...SOLUTIONS[s.key], score: s }));
  }, [scores]);

  const goNext = async () => {
    if (step === 6) {
      // Submit to backend before showing results
      setSubmitting(true);
      try {
        await fetch(`${API}/api/consulting/assessment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            persona: persona.id,
            personaName: persona.name,
            submittedAt: new Date().toISOString(),
          })
        });
      } catch (e) { console.error(e); }
      setSubmitting(false);
      setStep(7);
    } else {
      setStep(s => s + 1);
    }
  };

  const TOTAL_STEPS = 7;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="auto-lifestyle-quiz">
      {/* Progress bar — visible during quiz (steps 1-6) */}
      {step >= 1 && step <= 6 && (
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-gray-900">
          <div className="h-full bg-gradient-to-r from-red-600 to-amber-500 transition-all duration-500" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
        </div>
      )}

      {/* Step 0 — Lead Capture */}
      {step === 0 && (
        <div className="min-h-screen flex items-center justify-center p-6" data-testid="step-lead-capture">
          <div className="max-w-lg w-full">
            <div className="text-center mb-10">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-600 to-amber-500 flex items-center justify-center mx-auto mb-6">
                <MapPin className="h-7 w-7 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-black mb-4 leading-tight">Before We Map<br />Your Road...</h1>
              <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto">
                Transportation is connected to everything — your time, your stress, your freedom, your productivity. How you move through your day shapes your entire life.
              </p>
              <p className="text-gray-500 text-xs mt-4">This assessment takes 3–5 minutes. At the end, you'll receive your personal <span className="text-white font-semibold">Auto Lifestyle Profile</span>.</p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { icon: Car, label: 'Daily Life', sub: 'Commute & errands' },
                  { icon: Plane, label: 'Work & Travel', sub: 'Business & airports' },
                  { icon: Wine, label: 'Special Occasions', sub: 'Events & milestones' },
                ].map((c, i) => (
                  <div key={i} className="p-3 rounded-xl border border-gray-800 bg-gray-950">
                    <c.icon className="h-5 w-5 text-red-500 mx-auto mb-1.5" />
                    <p className="text-xs font-semibold">{c.label}</p>
                    <p className="text-[10px] text-gray-600">{c.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Where should we send your personalized roadmap?</p>
              <div className="grid grid-cols-2 gap-3">
                <Input value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="First Name" className="bg-gray-900 border-gray-700 text-white h-11" data-testid="field-firstName" />
                <Input value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Last Name" className="bg-gray-900 border-gray-700 text-white h-11" data-testid="field-lastName" />
              </div>
              <Input value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email Address" type="email" className="bg-gray-900 border-gray-700 text-white h-11" data-testid="field-email" />
              <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone (Optional)" className="bg-gray-900 border-gray-700 text-white h-11" />
            </div>
            <Button onClick={goNext} className="w-full bg-red-600 hover:bg-red-700 h-12 text-sm font-bold" data-testid="begin-btn"
              disabled={!form.firstName || !form.email}>
              Begin Assessment <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 1 — Identity */}
      {step === 1 && (
        <QuizSection step={1} total={TOTAL_STEPS} title="On a typical day, how do you prefer to move through your world?" sub="There's no wrong answer — just your truth about how you want to live." onBack={() => setStep(0)} onNext={goNext} canContinue={!!form.identity} testId="step-identity">
          {[
            { val: 'driven', label: 'I prefer to be Driven', desc: 'I want someone else handling the wheel. My time is better spent on everything else.' },
            { val: 'self', label: 'I prefer Driving myself', desc: 'I value independence and control over my own movement.' },
            { val: 'depends', label: 'It depends on the day and situation', desc: "Sometimes I drive. Sometimes I want to be driven. I need flexibility." },
            { val: 'unaware', label: "I haven't really thought about it", desc: 'I just do what I have to do to get where I need to go.' },
          ].map(o => (
            <OptionCard key={o.val} selected={form.identity === o.val} onClick={() => set('identity', o.val)} label={o.label} desc={o.desc} />
          ))}
        </QuizSection>
      )}

      {/* Step 2 — Daily Pain Points */}
      {step === 2 && (
        <QuizSection step={2} total={TOTAL_STEPS} title="What are your biggest daily transportation pain points?" sub="Select all that apply to your everyday reality." onBack={() => setStep(1)} onNext={goNext} canContinue={true} testId="step-pain-points" multi>
          {[
            { val: 'time', label: "I'm constantly losing time", desc: "Traffic, parking, and logistics eat into my day." },
            { val: 'stress', label: 'Driving stresses me out', desc: "Traffic, aggressive drivers, or just the mental load." },
            { val: 'cost', label: 'Transportation costs are unclear', desc: "Gas, parking, surge pricing — costs add up unpredictably." },
            { val: 'reliability', label: "I can't rely on my current solution", desc: "My car, rideshare, or whoever I depend on isn't consistent." },
            { val: 'productivity', label: "I can't work or rest while commuting", desc: "When I drive, I can't take calls, prep, or decompress." },
            { val: 'safety', label: 'Safety is a concern', desc: "At night, in unfamiliar areas, or when I've had drinks." },
          ].map(o => (
            <OptionCard key={o.val} selected={form.painPoints.includes(o.val)} onClick={() => toggleArr('painPoints', o.val)} label={o.label} desc={o.desc} multi />
          ))}
        </QuizSection>
      )}

      {/* Step 3 — Frequency */}
      {step === 3 && (
        <QuizSection step={3} total={TOTAL_STEPS} title="How often do you need professional transportation support?" sub="Think about your typical week — work, errands, appointments, and social plans." onBack={() => setStep(2)} onNext={goNext} canContinue={!!form.frequency} testId="step-frequency">
          {[
            { val: 'daily', label: 'Daily — This is a consistent need', desc: 'I need reliable transportation every weekday, sometimes weekends.' },
            { val: 'several', label: 'Several times a week', desc: 'Not every day, but transportation is a regular part of my life.' },
            { val: 'few', label: 'A few times a month', desc: 'Mostly for bigger trips, appointments, or routine changes.' },
            { val: 'special', label: 'Special occasions only', desc: 'Events, celebrations, airport runs — when the moment demands it.' },
          ].map(o => (
            <OptionCard key={o.val} selected={form.frequency === o.val} onClick={() => set('frequency', o.val)} label={o.label} desc={o.desc} />
          ))}
        </QuizSection>
      )}

      {/* Step 4 — Work & Business */}
      {step === 4 && (
        <QuizSection step={4} total={TOTAL_STEPS} title="Does transportation affect your professional performance?" sub="Select everything that reflects your current work-life reality." onBack={() => setStep(3)} onNext={goNext} canContinue={true} testId="step-work" multi>
          {[
            { val: 'airports', label: 'I travel for work — airports are regular', desc: 'I fly often enough that airport transport needs to be seamless.' },
            { val: 'clients', label: 'I transport clients or business associates', desc: 'The vehicle and service reflect on my brand.' },
            { val: 'productive', label: 'My commute should be productive time', desc: 'I need to prep, take calls, review documents.' },
            { val: 'fleet', label: 'My company could benefit from fleet or group transportation', desc: 'Multiple employees or executives need consistent, professional transport.' },
            { val: 'image', label: 'Arrival matters — I need to show up right', desc: 'How I arrive is part of my professional image.' },
            { val: 'none', label: "Work transportation isn't a factor for me", desc: 'My professional needs are covered or not applicable.' },
          ].map(o => (
            <OptionCard key={o.val} selected={form.workNeeds.includes(o.val)} onClick={() => toggleArr('workNeeds', o.val)} label={o.label} desc={o.desc} multi />
          ))}
        </QuizSection>
      )}

      {/* Step 5 — Life's Moments */}
      {step === 5 && (
        <QuizSection step={5} total={TOTAL_STEPS} title="Which of life's moments do you want handled with excellence?" sub="These are the moments where the experience matters as much as the destination." onBack={() => setStep(4)} onNext={goNext} canContinue={true} testId="step-occasions" multi>
          {[
            { val: 'weddings', label: 'Weddings', desc: 'The most important day. Nothing should go wrong.' },
            { val: 'prom', label: 'Prom / Graduations', desc: 'Milestone moments that deserve a luxury arrival.' },
            { val: 'birthdays', label: 'Birthdays & Celebrations', desc: 'Group nights out with zero logistical stress.' },
            { val: 'dining', label: 'Fine Dining & Date Nights', desc: 'Arrive without parking headaches, leave without limits.' },
            { val: 'concerts', label: 'Concerts & Events', desc: 'Skip the traffic. Enjoy every moment.' },
            { val: 'group', label: 'Group Travel', desc: 'Multiple people, multiple stops — coordinated seamlessly.' },
            { val: 'vip', label: 'VIP / Executive Transport', desc: 'When the guest or client deserves the best.' },
            { val: 'medical', label: 'Medical & Wellness', desc: 'Trusted, discreet transport when health is the priority.' },
          ].map(o => (
            <OptionCard key={o.val} selected={form.occasions.includes(o.val)} onClick={() => toggleArr('occasions', o.val)} label={o.label} desc={o.desc} multi />
          ))}
        </QuizSection>
      )}

      {/* Step 6 — Ratings */}
      {step === 6 && (
        <div className="min-h-screen flex items-center justify-center p-6 pt-10" data-testid="step-ratings">
          <div className="max-w-lg w-full">
            <p className="text-[10px] font-bold text-gray-600 tracking-widest uppercase mb-2">Section 6 of {TOTAL_STEPS}</p>
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Rate what matters most to you in a transportation experience.</h2>
            <p className="text-sm text-gray-500 mb-8">1 = Least important | 5 = Non-negotiable</p>

            <div className="space-y-6 mb-10">
              {[
                { key: 'time', icon: Clock, label: 'Time — I want those hours back', color: '#3b82f6' },
                { key: 'stress', icon: Heart, label: 'Stress Reduction — I want to arrive calm', color: '#8b5cf6' },
                { key: 'cost', icon: DollarSign, label: 'Cost Management — Predictable, fair pricing', color: '#22c55e' },
                { key: 'luxury', icon: Sparkles, label: 'Luxury & Experience — The journey is part of it', color: '#eab308' },
                { key: 'safety', icon: Shield, label: 'Safety & Trust — I need to know I\'m in good hands', color: '#ef4444' },
              ].map(r => (
                <div key={r.key} className="p-4 rounded-xl border border-gray-800 bg-gray-950">
                  <div className="flex items-center gap-2 mb-3">
                    <r.icon className="h-4 w-4" style={{ color: r.color }} />
                    <p className="text-sm font-semibold">{r.label}</p>
                  </div>
                  <div className="flex gap-2">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setRating(r.key, n)} type="button"
                        className={`flex-1 h-11 rounded-lg text-sm font-bold transition-all ${
                          form.ratings[r.key] === n
                            ? 'text-white shadow-lg scale-105'
                            : form.ratings[r.key] > n
                              ? 'text-white/80'
                              : 'bg-gray-800 text-gray-600 hover:bg-gray-700'
                        }`}
                        style={form.ratings[r.key] >= n ? { backgroundColor: r.color } : undefined}
                        data-testid={`rating-${r.key}-${n}`}
                      >{n}</button>
                    ))}
                  </div>
                  <div className="flex justify-between mt-1"><span className="text-[9px] text-gray-700">Low</span><span className="text-[9px] text-gray-700">High</span></div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <Button onClick={() => setStep(5)} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs" data-testid="btn-back">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
              </Button>
              <Button onClick={goNext} className="bg-gradient-to-r from-red-600 to-amber-500 hover:from-red-700 hover:to-amber-600 text-sm font-bold h-11 px-6" disabled={submitting} data-testid="btn-reveal">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MapPin className="h-4 w-4 mr-2" />}
                Reveal My Auto Lifestyle Profile
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 7 — RESULTS */}
      {step === 7 && (
        <div className="min-h-screen" data-testid="step-results">
          {/* Results Hero */}
          <div className="bg-gradient-to-b from-gray-950 to-black py-16 px-6 text-center">
            <p className="text-red-500 text-[10px] font-bold tracking-[0.3em] uppercase mb-4">Your Auto Lifestyle Profile</p>
            <p className="text-xs text-gray-600 mb-2">You Are</p>
            <h1 className="text-3xl sm:text-4xl font-black mb-4" data-testid="persona-name">{persona.name}</h1>
            <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">{persona.desc}</p>
          </div>

          <div className="max-w-2xl mx-auto px-6 py-10 space-y-10">
            {/* Lifestyle Scores */}
            <div>
              <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Your Auto Lifestyle Scores</h3>
              <div className="space-y-3">
                {scores.map(s => (
                  <div key={s.key} className="flex items-center gap-3">
                    <s.icon className="h-4 w-4 shrink-0" style={{ color: s.color }} />
                    <span className="text-xs text-gray-400 w-32 shrink-0">{s.label}</span>
                    <div className="flex-1 h-3 bg-gray-900 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${s.val * 20}%`, backgroundColor: s.color }} />
                    </div>
                    <span className="text-xs font-bold w-6 text-right" style={{ color: s.color }}>{s.val}/5</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pain Points */}
            {form.painPoints.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Your Key Pain Points</h3>
                <div className="flex flex-wrap gap-2">
                  {form.painPoints.map(p => {
                    const labels = { time: 'Losing Time', stress: 'Driving Stress', cost: 'Unclear Costs', reliability: 'Unreliable Transport', productivity: 'Wasted Commute', safety: 'Safety Concerns' };
                    return <span key={p} className="px-3 py-1.5 rounded-full bg-red-600/15 text-red-400 text-xs font-semibold border border-red-600/20">{labels[p]}</span>;
                  })}
                </div>
              </div>
            )}

            {/* Recommended Solutions */}
            <div>
              <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Your ACE Roadmap — Recommended Solutions</h3>
              <div className="space-y-3">
                {topSolutions.map((sol, i) => (
                  <button key={i} onClick={() => navigate(sol.route)} className="w-full text-left p-4 rounded-xl border border-gray-800 bg-gray-950 hover:border-gray-700 transition-all group" data-testid={`solution-${i}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-white group-hover:text-red-400 transition-colors">{sol.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{sol.desc}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-700 group-hover:text-red-500 transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center pt-6 border-t border-gray-800">
              <h3 className="text-xl font-bold mb-2">Ready to Elevate Your Auto Lifestyle?</h3>
              <p className="text-sm text-gray-500 mb-6">ACE delivers relief. The client should never need to ask. Let's build your personal transportation solution.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="tel:6788463009" className="inline-flex items-center justify-center h-12 px-6 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors" data-testid="cta-call">
                  <Phone className="h-4 w-4 mr-2" /> Call ACE — (678) 846-3009
                </a>
                <Button onClick={() => navigate('/consultation/intake')} variant="outline" className="h-12 px-6 border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-bold" data-testid="cta-consult">
                  Start Consulting Profile <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Membership Tiers */}
            <div className="pt-6">
              <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4 text-center">ACE Membership Tiers</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { tier: 'Lobby', level: 'Entry Level', desc: 'Essential concierge transportation for occasional needs', highlight: false },
                  { tier: 'Condo', level: 'Mid Level', desc: 'Priority service, flexible scheduling, and member benefits', highlight: true },
                  { tier: 'Penthouse', level: 'Elite Level', desc: 'Full concierge access, dedicated driver, white-glove everything', highlight: false },
                ].map((m, i) => (
                  <div key={i} className={`p-4 rounded-xl border text-center ${m.highlight ? 'border-red-600 bg-red-600/5' : 'border-gray-800 bg-gray-950'}`}>
                    <p className="text-xs text-gray-600 font-bold uppercase tracking-wider">{m.tier}</p>
                    <p className="text-sm font-bold mt-1">{m.level}</p>
                    <p className="text-xs text-gray-500 mt-1">{m.desc}</p>
                    {m.highlight && <span className="inline-block mt-2 text-[9px] text-red-500 font-bold">POPULAR</span>}
                  </div>
                ))}
              </div>
              <div className="text-center mt-4">
                <Button onClick={() => navigate('/memberships')} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs" data-testid="cta-membership">
                  Inquire About Membership
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══ Shared Components ═══ */

const QuizSection = ({ step, total, title, sub, onBack, onNext, canContinue, testId, children, multi }) => (
  <div className="min-h-screen flex items-center justify-center p-6 pt-10" data-testid={testId}>
    <div className="max-w-lg w-full">
      <p className="text-[10px] font-bold text-gray-600 tracking-widest uppercase mb-2">Section {step} of {total}</p>
      <h2 className="text-xl sm:text-2xl font-bold mb-2">{title}</h2>
      {sub && <p className="text-sm text-gray-500 mb-6">{multi ? 'Select all that apply.' : sub}</p>}
      <div className="space-y-3 mb-8">{children}</div>
      <div className="flex items-center justify-between">
        <Button onClick={onBack} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs" data-testid="btn-back">
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
        </Button>
        <Button onClick={onNext} className="bg-red-600 hover:bg-red-700 text-xs" disabled={!canContinue} data-testid="btn-continue">
          Continue <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </div>
    </div>
  </div>
);

const OptionCard = ({ selected, onClick, label, desc, multi }) => (
  <button onClick={onClick} type="button"
    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selected ? 'border-red-600 bg-red-600/5' : 'border-gray-800 hover:border-gray-700'}`}>
    <div className="flex items-start gap-3">
      <div className={`w-5 h-5 mt-0.5 shrink-0 flex items-center justify-center ${multi ? 'rounded border' : 'rounded-full border-2'} ${
        selected ? (multi ? 'bg-red-600 border-red-600' : 'border-red-500') : 'border-gray-700'
      }`}>
        {selected && (multi ? <CheckCircle className="h-3.5 w-3.5 text-white" /> : <div className="w-2.5 h-2.5 rounded-full bg-red-500" />)}
      </div>
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
      </div>
    </div>
  </button>
);

export default AutoLifestyleQuiz;
